/**
 * Hook + helpers "view-as-expert" :
 * - L'utilisateur connecte est-il un admin (whitelist UPN) ?
 * - S'il l'est, il peut choisir un expertId via lequel filtrer la vue BO.
 *
 * Stockage : localStorage cle `stella-view-as-expert`.
 * Ecoute multi-onglets via storage event.
 */
import { useCallback, useEffect, useState } from 'react';

/** UPN autorisees a basculer la vue. Ajoute d'autres admins ici si besoin. */
export const ADMIN_UPNS = new Set<string>(['rk@stella-cars.com', 'sn@stella-cars.com']);

/**
 * Expert responsable du showroom (Marc-Olivier "Marco") : seul expert habilite
 * a gerer les fermetures exceptionnelles de la concession. Les admins y
 * accedent en passant par son profil (view-as marc-olivier).
 */
export const SHOWROOM_MANAGER_UPN = 'mop@stella-cars.com';
export const SHOWROOM_MANAGER_EXPERT_ID = 'marc-olivier';

export function isShowroomManagerUpn(upn: string | undefined | null): boolean {
  return (upn ?? '').toLowerCase() === SHOWROOM_MANAGER_UPN;
}

/**
 * Le panneau "Fermetures exceptionnelles" est visible pour :
 *  - Marco lui-meme (responsable showroom), ou
 *  - un admin (rk/sn) qui consulte le profil de Marco via view-as.
 */
export function canSeeClosures(upn: string | undefined | null, viewAsExpertId: string | null): boolean {
  if (isShowroomManagerUpn(upn)) return true;
  return isAdminUpn(upn) && viewAsExpertId === SHOWROOM_MANAGER_EXPERT_ID;
}

const STORAGE_KEY = 'stella-view-as-expert';

export function getStoredViewAsExpert(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredViewAsExpert(expertId: string | null): void {
  try {
    if (!expertId) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, expertId);
    // Notifie le tab courant (storage event ne tire que sur les autres onglets)
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
  } catch {
    /* ignore */
  }
}

export function isAdminUpn(upn: string | undefined | null): boolean {
  if (!upn) return false;
  return ADMIN_UPNS.has(upn.toLowerCase());
}

/**
 * Hook React : retourne l'expertId courant (impersonation). Reactif sur les
 * changements localStorage (multi-onglets + dispatch local).
 */
export function useViewAsExpert(): { expertId: string | null; setExpertId: (id: string | null) => void } {
  const [expertId, setLocal] = useState<string | null>(() => getStoredViewAsExpert());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLocal(getStoredViewAsExpert());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setExpertId = useCallback((id: string | null) => {
    setStoredViewAsExpert(id);
    setLocal(id);
  }, []);

  return { expertId, setExpertId };
}
